import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtDate, STATUS_STYLES, statusLabel, fileUrl } from "@/lib/api";

export const PageHeader = ({ title, subtitle, action, testId }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-5 fade-up" data-testid={testId}>
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {action && <div className="flex gap-2">{action}</div>}
  </div>
);

export const StatusBadge = ({ status, className }) => (
  <span data-testid={`status-badge-${status}`} className={cn("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap", STATUS_STYLES[status] || "bg-slate-100 text-slate-600", className)}>
    {statusLabel(status)}
  </span>
);

// Shared "Imported" indicator. Shown only when the record itself carries import metadata (imported_at).
export const isImported = (doc) => Boolean(doc?.imported_at || doc?.imported);
export const ImportBadge = ({ doc, className }) => isImported(doc) ? (
  <span data-testid={doc.historical_import ? "historical-import-badge" : "imported-badge"} className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap bg-slate-100 text-slate-500 border border-slate-200", className)}>
    {doc.historical_import ? "Historical Import" : "Imported"}
  </span>
) : null;

export const ImportMeta = ({ doc, className }) => isImported(doc) ? (
  <p className={cn("text-xs text-slate-400", className)} data-testid="import-meta">{doc.historical_import ? "Historical migration" : "Imported record"} · Imported {fmtDate(doc.imported_at)}{doc.import_source === "neec_assisted" ? " · Migrated by NEEC Digital" : ""}</p>
) : null;

// Approximate historical dates: never show the stored placeholder as if it were the exact day.
export const APPROX_HINT = { month: "Historical date · exact day not provided", year: "Historical date · exact month/day not provided", unknown: "Historical date not provided" };
export const docDate = (doc, key) => {
  const p = doc?.date_precision;
  if (p && p !== "exact") return doc.date_display || (p === "unknown" ? "Date unknown" : "");
  return fmtDate(doc?.[key]);
};
export const DocDate = ({ doc, dateKey, className, withHint = false }) => {
  const p = doc?.date_precision;
  const approx = p && p !== "exact";
  return <span className={className} data-testid={approx ? "approx-date" : "doc-date"} title={approx ? APPROX_HINT[p] : undefined}>{docDate(doc, dateKey)}{withHint && approx && <span className="block text-[11px] text-slate-400 font-normal">{APPROX_HINT[p]}</span>}</span>;
};

export const Money = ({ value, className, currency }) => <span className={cn("tabular-nums font-semibold", className)}>{fmtMoney(value, currency)}</span>;

export const EmptyState = ({ title, hint, action }) => (
  <div className="card-flat p-10 text-center" data-testid="empty-state">
    <p className="font-semibold text-slate-800">{title}</p>
    {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Field = ({ label, children, hint, className }) => (
  <label className={cn("block", className)}>
    <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
  </label>
);

export const inputCls = "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition";
export const selectCls = inputCls + " appearance-none";
export const textareaCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition min-h-[90px]";

export const Logo = ({ business, size = 48, className }) =>
  business?.logo_file_id ? (
    <img src={fileUrl(business.logo_file_id)} alt={business.business_name} width={size} height={size} className={cn("rounded-lg object-cover", className)} style={{ width: size, height: size }} />
  ) : (
    <div className={cn("rounded-lg bg-[#0B192C] text-white font-display font-bold grid place-items-center", className)} style={{ width: size, height: size }}>
      {(business?.business_name || "N").slice(0, 1)}
    </div>
  );

export const LineMeta = ({ li }) => {
  if (li.type === "hourly") return <>{Number(li.quantity)} hrs x {fmtMoney(li.unit_price)}/hr</>;
  if (li.type === "flat") return <>Flat rate</>;
  return <>{Number(li.quantity)} x {fmtMoney(li.unit_price)}</>;
};

export const TotalsBlock = ({ doc, extra }) => (
  <div className="ml-auto w-full sm:w-72 text-sm space-y-1.5" data-testid="totals-block">
    <div className="flex justify-between text-slate-600"><span>Subtotal</span><Money value={doc.subtotal} className="font-medium" /></div>
    {doc.discount_amount > 0 && <div className="flex justify-between text-slate-600"><span>Discount</span><span className="tabular-nums">-{fmtMoney(doc.discount_amount)}</span></div>}
    {doc.tax_amount > 0 && <div className="flex justify-between text-slate-600"><span>Tax ({Number(doc.tax_rate)}%)</span><Money value={doc.tax_amount} className="font-medium" /></div>}
    <div className="flex justify-between pt-2 border-t border-slate-200 text-lg font-bold"><span>Total</span><Money value={doc.total} data-testid="doc-total" /></div>
    {extra}
  </div>
);

export const DocumentView = ({ kind, doc, customer, business, badge }) => {
  const isInv = kind === "invoice";
  const addr = [business?.address, business?.city, [business?.state, business?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <div className="print-doc bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-10 max-w-3xl mx-auto" data-testid={`${kind}-document`}>
      <div className="flex flex-wrap justify-between gap-4 pb-6 border-b-2 border-orange-500">
        <div className="flex gap-3 items-start">
          <Logo business={business} size={56} />
          <div>
            <div className="font-display text-2xl font-bold text-[#0B192C] leading-tight" data-testid="doc-business-name">{business?.business_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{addr}</div>
            <div className="text-xs text-slate-500">{[business?.phone, business?.email].filter(Boolean).join(" | ")}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-[#0B192C] tracking-wide">{isInv ? "INVOICE" : "QUOTE"}</div>
          <div className="font-semibold text-slate-800" data-testid="doc-number">{doc.number}</div>
          <div className="text-xs text-slate-500">{isInv ? "Issued" : "Date"} {doc.issue_date || (doc.created_at || "").slice(0, 10)}</div>
          {badge && <div className="mt-2 flex justify-end">{badge}</div>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 py-6 text-sm">
        <div>
          <div className="eyebrow mb-1">{isInv ? "Bill to" : "Prepared for"}</div>
          <div className="font-semibold text-slate-900">{customer?.name}</div>
          <div className="text-slate-600">{customer?.billing_address || customer?.service_address}</div>
          <div className="text-slate-600">{customer?.phone}</div>
          <div className="text-slate-600">{customer?.email}</div>
        </div>
        <div>
          <div className="eyebrow mb-1">Service address</div>
          <div className="text-slate-700">{doc.job_address || customer?.service_address}</div>
          {doc.scheduled_date && <div className="text-slate-600 mt-1">Scheduled: {doc.scheduled_date} {doc.scheduled_time || ""}</div>}
          {isInv && <div className="text-slate-600 mt-1">Due: <span className="font-medium">{doc.due_date}</span> | {doc.payment_terms}</div>}
          {!isInv && doc.expires_at && <div className="text-slate-600 mt-1">Valid until {doc.expires_at}</div>}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#0B192C] text-white text-left">
            <th className="py-2.5 px-3 rounded-l-lg font-semibold">Description</th>
            <th className="py-2.5 px-3 text-right font-semibold hidden sm:table-cell">Qty / Rate</th>
            <th className="py-2.5 px-3 text-right rounded-r-lg font-semibold">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(doc.line_items || []).map((li) => (
            <tr key={li.id} className="border-b border-slate-100" data-testid="doc-line-item">
              <td className="py-3 px-3">
                <div className="font-medium text-slate-900">{li.description}</div>
                <div className="text-xs text-slate-500 sm:hidden"><LineMeta li={li} /></div>
              </td>
              <td className="py-3 px-3 text-right text-slate-600 hidden sm:table-cell"><LineMeta li={li} /></td>
              <td className="py-3 px-3 text-right"><Money value={li.total} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex mt-4">
        <TotalsBlock doc={doc} extra={isInv && (
          <>
            <div className="flex justify-between text-slate-600"><span>Paid</span><span className="tabular-nums">-{fmtMoney(doc.amount_paid)}</span></div>
            <div className="flex justify-between font-bold text-orange-600"><span>Balance due</span><Money value={doc.balance_due} /></div>
          </>
        )} />
      </div>
      {doc.notes && <div className="mt-6 text-sm"><div className="eyebrow mb-1">Notes</div><p className="text-slate-700 whitespace-pre-line">{doc.notes}</p></div>}
      {doc.terms && <div className="mt-4 text-xs text-slate-500"><div className="eyebrow mb-1">Terms</div><p className="whitespace-pre-line">{doc.terms}</p></div>}
      {isInv && doc.payments?.length > 0 && (
        <div className="mt-6 text-sm">
          <div className="eyebrow mb-1">Payments</div>
          {doc.payments.map((p) => <div key={p.id} className="flex justify-between text-slate-700 py-1 border-b border-slate-100"><span>{p.date} | {statusLabel(p.method)} {p.reference ? `| ${p.reference}` : ""}</span><Money value={p.amount} /></div>)}
        </div>
      )}
      <p className="mt-8 text-xs text-slate-400 text-center">Thank you for choosing {business?.business_name}.</p>
    </div>
  );
};

export const Loading = ({ full }) => (
  <div className={cn("grid place-items-center text-slate-500 text-sm", full ? "min-h-screen" : "py-24")} data-testid="loading-state">
    <div className="flex items-center gap-3"><span className="w-5 h-5 rounded-full border-2 border-slate-300 border-t-orange-500 animate-spin" />Loading...</div>
  </div>
);

export const ErrorState = ({ message = "Something went wrong. Please try again.", onRetry }) => (
  <div className="card-flat p-10 text-center" data-testid="error-state">
    <p className="font-semibold text-slate-800">We hit a snag</p>
    <p className="text-sm text-slate-500 mt-1">{message}</p>
    {onRetry && <button onClick={onRetry} className="mt-4 text-sm font-semibold text-orange-600">Try again</button>}
  </div>
);

export const CustomerLink = ({ customer, className }) => customer?.id ? <Link to={`/customers/${customer.id}`} className={cn("hover:text-orange-600 hover:underline", className)}>{customer.name}</Link> : <span className={className}>{customer?.name || "Walk-in"}</span>;
